import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, useMediaQuery, ImageList, ImageListItem, Typography, TextField, Checkbox } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import VideoPlaceholder from './videoplaceholder';
import { useTranslation } from 'react-i18next';
import VideoDialog from './videodialog';
import { getVideoUrl } from '../modules/surveydata';
import { useDispatch } from 'react-redux';

export default function VideoList({ videos, editMode, selectedVideos, videoCommentChange, videoSelectChange }) {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const playVideo = async (videoId) => {
        const videoUrl = await getVideoUrl(videoId)(dispatch);
        setSelectedVideoUrl(videoUrl);
    };

    const onVideoNoteChange = (event, videoId) => {
        videoCommentChange(videoId, event.target.value);
    };

    const onVideoSelectChange = (videoId, checked) => {
        videoSelectChange(videoId, checked);
    };

    if (!editMode) {
        // view mode
        return (
            <ImageList
                cols={isSmallScreen ? 1 : undefined}
                sx={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                    gap: 2,
                    gridAutoFlow: 'row',
                }}>
                {videos.map((video) => (
                    <ImageListItem key={video.videoId} sx={{ width: '100%' }}>
                        <VideoPlaceholder onClick={() => playVideo(video.videoId)} />
                        <Box sx={{ p: 1 }}>
                            {video.comments && (
                                <Typography variant="body2" color="textSecondary">
                                    {video.comments}
                                </Typography>
                            )}
                        </Box>
                    </ImageListItem>
                ))}
                {selectedVideoUrl && (
                    <VideoDialog videoUrl={selectedVideoUrl} onClose={() => setSelectedVideoUrl(null)} />
                )}
            </ImageList>
        );
    } else {
        // edit mode
        return (
            <ImageList
                cols={isSmallScreen ? 1 : undefined}
                sx={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                    gap: 2,
                    gridAutoFlow: 'row',
                }}>
                {videos.map((video) => (
                    <ImageListItem key={video.videoId} sx={{ width: '100%' }}>
                        <VideoPlaceholder onClick={() => playVideo(video.videoId)} />
                        <Box sx={{ p: 1 }}>
                            <TextField
                                data-testid={`video-note-${video.videoId}`}
                                variant="outlined"
                                size="small"
                                multiline
                                maxRows={3}
                                label={t('survey.comments')}
                                defaultValue={video.comments}
                                name="mvideo-notes"
                                onBlur={(e) => {
                                    onVideoNoteChange(e, video.videoId);
                                }}
                                sx={{ width: 'auto', minWidth: 0 }}
                            />
                        </Box>
                        <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Checkbox
                                data-testid={`video-${video.videoId}-include-checkbox`}
                                checked={selectedVideos.includes(video.videoId)}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                    onVideoSelectChange(video.videoId, e.target.checked);
                                }}
                            />
                            <Typography>{t('survey.includetopdf')}</Typography>
                        </Box>
                    </ImageListItem>
                ))}
                {selectedVideoUrl && (
                    <VideoDialog videoUrl={selectedVideoUrl} onClose={() => setSelectedVideoUrl(null)} />
                )}
            </ImageList>
        );
    }
}

VideoList.propTypes = {
    videos: PropTypes.arrayOf(
        PropTypes.shape({
            videoId: PropTypes.string.isRequired,
            comments: PropTypes.string,
        })
    ).isRequired,
    editMode: PropTypes.bool,
    selectedVideos: PropTypes.arrayOf(PropTypes.string),
    videoCommentChange: PropTypes.func,
    videoSelectChange: PropTypes.func,
};

VideoList.defaultProps = {
    editMode: false,
    selectedVideos: [],
    videoCommentChange: () => {},
    videoSelectChange: () => {},
};
